import { createBrowserRouter } from "react-router-dom";

import { ShopLayout } from "@/layouts/ShopLayout";
import { AccountLayout } from "@/layouts/AccountLayout";
import { AdminLayout } from "@/layouts/AdminLayout";
import { AdminRouteGuard } from "@/components/admin/AdminRouteGuard";

import { HomePage } from "@/pages/shop/HomePage";
import { AProposPage } from "@/pages/shop/AProposPage";
import { CataloguePage } from "@/pages/shop/CataloguePage";
import { CguPage } from "@/pages/shop/CguPage";
import { CgvPage } from "@/pages/shop/CgvPage";
import { CommandePage } from "@/pages/shop/CommandePage";
import { CommandeConfirmationPage } from "@/pages/shop/CommandeConfirmationPage";
import { ConfidentialitePage } from "@/pages/shop/ConfidentialitePage";
import { ContactPage } from "@/pages/shop/ContactPage";
import { FaqPage } from "@/pages/shop/FaqPage";
import { LivraisonPage } from "@/pages/shop/LivraisonPage";
import { MarquesPage } from "@/pages/shop/MarquesPage";
import { MarqueDetailPage } from "@/pages/shop/MarqueDetailPage";
import { MentionsLegalesPage } from "@/pages/shop/MentionsLegalesPage";
import { ProductDetailPage } from "@/pages/shop/ProductDetailPage";
import { PaiementPage } from "@/pages/shop/PaiementPage";
import { PanierPage } from "@/pages/shop/PanierPage";
import { PolitiqueCookiesPage } from "@/pages/shop/PolitiqueCookiesPage";
import { RecherchePage } from "@/pages/shop/RecherchePage";
import { RetoursPage } from "@/pages/shop/RetoursPage";
import { NotFoundPage } from "@/pages/shop/NotFoundPage";
import { OffrePage } from "@/pages/shop/OffrePage";

import { ConnexionPage } from "@/pages/account/ConnexionPage";
import { ComptePage } from "@/pages/account/ComptePage";
import { CompteCommandesPage } from "@/pages/account/CompteCommandesPage";
import { CompteCommandeDetailPage } from "@/pages/account/CompteCommandeDetailPage";
import { CompteFavorisPage } from "@/pages/account/CompteFavorisPage";

import { AdminDashboardPage } from "@/pages/admin/AdminDashboardPage";
import { AdminConnexionPage } from "@/pages/admin/AdminConnexionPage";
import { AdminCommandesPage } from "@/pages/admin/AdminCommandesPage";
import { AdminCommandeDetailPage } from "@/pages/admin/AdminCommandeDetailPage";
import { AdminCataloguePage } from "@/pages/admin/AdminCataloguePage";
import { AdminCatalogueCategoriesPage } from "@/pages/admin/AdminCatalogueCategoriesPage";
import { AdminCatalogueMarquesPage } from "@/pages/admin/AdminCatalogueMarquesPage";
import { AdminCatalogueProduitsPage } from "@/pages/admin/AdminCatalogueProduitsPage";
import { AdminCatalogueProduitNouveauPage } from "@/pages/admin/AdminCatalogueProduitNouveauPage";
import { AdminCatalogueProduitDetailPage } from "@/pages/admin/AdminCatalogueProduitDetailPage";
import { AdminClientsPage } from "@/pages/admin/AdminClientsPage";
import { AdminClientDetailPage } from "@/pages/admin/AdminClientDetailPage";
import { AdminStockPage } from "@/pages/admin/AdminStockPage";
import { AdminParametresPage } from "@/pages/admin/AdminParametresPage";

/**
 * React Router v7 en mode "data" (`createBrowserRouter`), PAS en mode
 * "framework" (pas de `@react-router/dev`, pas de plugin Vite dédié, pas de
 * SSR) — conformément à la décision d'architecture de la tâche de
 * scaffolding (SPA statique ; une passe de prérendu séparée viendra plus
 * tard). Aucun `loader`/`action` de route à ce lot : le chargement de données
 * passe par TanStack Query directement dans les composants (cf.
 * src/lib/query-client.ts), pas par le cycle de vie du routeur.
 *
 * Trois layouts distincts, un par grande zone du site (Shop / Compte / Admin)
 * — cf. rapport de la tâche de scaffolding pour la justification du choix
 * "Compte imbriqué dans Shop, Admin indépendant" (écart assumé par rapport à
 * apps/web, où le layout racine unique enveloppe tout, y compris l'admin).
 *
 * Chemins vérifiés un par un par lecture directe de la structure réelle de
 * apps/web/app/ (dossiers `(shop)`, `(account)`, `(admin)`), y compris les
 * segments dynamiques (`[slug]`, `[id]`, `[orderNumber]`) et le catch-all
 * optionnel `[[...slug]]` de `/catalogue`.
 */
export const router = createBrowserRouter([
  {
    element: <ShopLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "a-propos", element: <AProposPage /> },
      // Catch-all optionnel Next.js (`[[...slug]]`) : deux entrées pour couvrir
      // "/catalogue" (rien après) ET "/catalogue/a/b/c" (segments wildcard).
      { path: "catalogue", element: <CataloguePage /> },
      { path: "catalogue/*", element: <CataloguePage /> },
      { path: "cgu", element: <CguPage /> },
      { path: "cgv", element: <CgvPage /> },
      { path: "commande", element: <CommandePage /> },
      { path: "commande/confirmation", element: <CommandeConfirmationPage /> },
      { path: "confidentialite", element: <ConfidentialitePage /> },
      { path: "contact", element: <ContactPage /> },
      { path: "faq", element: <FaqPage /> },
      { path: "livraison", element: <LivraisonPage /> },
      { path: "marques", element: <MarquesPage /> },
      { path: "marques/:slug", element: <MarqueDetailPage /> },
      { path: "mentions-legales", element: <MentionsLegalesPage /> },
      { path: "p/:slug", element: <ProductDetailPage /> },
      { path: "paiement", element: <PaiementPage /> },
      { path: "panier", element: <PanierPage /> },
      { path: "politique-cookies", element: <PolitiqueCookiesPage /> },
      { path: "recherche", element: <RecherchePage /> },
      { path: "retours", element: <RetoursPage /> },

      // Espace client (apps/web app/(account)/**) — partage le chrome boutique
      // ci-dessus, cf. AccountLayout.
      {
        element: <AccountLayout />,
        children: [
          { path: "connexion", element: <ConnexionPage /> },
          { path: "compte", element: <ComptePage /> },
          { path: "compte/commandes", element: <CompteCommandesPage /> },
          { path: "compte/commandes/:orderNumber", element: <CompteCommandeDetailPage /> },
          { path: "compte/favoris", element: <CompteFavorisPage /> },
        ],
      },

      { path: "*", element: <NotFoundPage /> },
    ],
  },
  // Pages d'atterrissage publicitaire. Volontairement AU MÊME NIVEAU que
  // ShopLayout et non dedans : elles n'affichent ni la navigation boutique, ni
  // la barre d'onglets mobile, ni le bouton WhatsApp flottant — un visiteur
  // qui arrive d'une publicité n'a qu'une seule action à faire, et la barre
  // d'onglets fixe entrait en collision avec le bouton « Commander » fixe de
  // la page (cf. `CampaignShell` dans OffrePage.tsx).
  { path: "/offre/:slug", element: <OffrePage /> },
  {
    path: "/admin",
    element: <AdminLayout />,
    children: [
      // Seule route admin NON protégée par AdminRouteGuard (cf. son propre
      // commentaire) — une garde inconditionnelle ici boucherait sur elle-même.
      { path: "connexion", element: <AdminConnexionPage /> },
      { index: true, element: <AdminRouteGuard><AdminDashboardPage /></AdminRouteGuard> },
      { path: "commandes", element: <AdminRouteGuard><AdminCommandesPage /></AdminRouteGuard> },
      { path: "commandes/:id", element: <AdminRouteGuard><AdminCommandeDetailPage /></AdminRouteGuard> },
      { path: "catalogue", element: <AdminRouteGuard><AdminCataloguePage /></AdminRouteGuard> },
      { path: "catalogue/categories", element: <AdminRouteGuard><AdminCatalogueCategoriesPage /></AdminRouteGuard> },
      { path: "catalogue/marques", element: <AdminRouteGuard><AdminCatalogueMarquesPage /></AdminRouteGuard> },
      { path: "catalogue/produits", element: <AdminRouteGuard><AdminCatalogueProduitsPage /></AdminRouteGuard> },
      {
        path: "catalogue/produits/nouveau",
        element: <AdminRouteGuard><AdminCatalogueProduitNouveauPage /></AdminRouteGuard>,
      },
      {
        path: "catalogue/produits/:id",
        element: <AdminRouteGuard><AdminCatalogueProduitDetailPage /></AdminRouteGuard>,
      },
      { path: "clients", element: <AdminRouteGuard><AdminClientsPage /></AdminRouteGuard> },
      { path: "clients/:id", element: <AdminRouteGuard><AdminClientDetailPage /></AdminRouteGuard> },
      { path: "stock", element: <AdminRouteGuard><AdminStockPage /></AdminRouteGuard> },
      { path: "parametres", element: <AdminRouteGuard><AdminParametresPage /></AdminRouteGuard> },
    ],
  },
]);
